import { useCallback, useEffect, useState } from "react";

/**
 * Hook for per-user column visibility, persisted in localStorage.
 *
 * - Pass the list of `togglableKeys` (the keys that the user is allowed to
 *   show/hide). Any column whose `key` is NOT in this list is always visible
 *   (e.g. anchor/action columns). Columns without a `key` are also always
 *   visible.
 * - The first time, `defaultVisibleKeys` (or all togglable keys if omitted)
 *   are visible.
 * - The selection is stored as an array of visible keys under `storageKey`.
 *   Unknown keys from older versions are discarded so a key rename doesn't
 *   surface ghost entries.
 */
export interface UseColumnVisibilityOptions {
  storageKey: string;
  togglableKeys: string[];
  defaultVisibleKeys?: string[];
}

export function useColumnVisibility({
  storageKey,
  togglableKeys,
  defaultVisibleKeys,
}: UseColumnVisibilityOptions) {
  const initial = defaultVisibleKeys ?? togglableKeys;

  const [visibleKeys, setVisibleKeys] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return initial;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return initial;
      return parsed.filter(
        (k: any) => typeof k === "string" && togglableKeys.includes(k),
      );
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(visibleKeys));
    } catch {
      // ignore quota / private mode errors
    }
  }, [storageKey, visibleKeys]);

  const isVisible = useCallback(
    (key?: string) => {
      if (!key) return true;
      if (!togglableKeys.includes(key)) return true;
      return visibleKeys.includes(key);
    },
    [togglableKeys, visibleKeys],
  );

  return { visibleKeys, setVisibleKeys, isVisible };
}
